import { useCallback } from 'react';
import { InView } from 'react-intersection-observer';
import {
  Heading,
  TextArea,
  ToggleButtonPill,
  ToggleButtonPillGroup,
} from '@hungryroot/ramen';
import { tags } from '@hungryroot/tags';
import { foodFeedbackIdMap } from '../../../hooks/useFoodFeedback';
import { type useDetectChanges } from '../useDetectChanges';
import {
  type RatablePairingItem,
  type RatableProductItem,
} from '../useRatableItems';
import { useIngredientFeedback } from './useIngredientFeedback';
import { useNutritionalTagFeedback } from './useNutritionalTagFeedback';
import { useTextAreaFeedback } from './useTextAreaFeedback';
import styles from './FoodFeedbackSecondary.module.css';

type FoodFeedbackSecondaryProps = {
  foodFeedbackSelection: number | null;
  rating: boolean;
  item: RatablePairingItem | RatableProductItem;
  productRatingId?: number | null;
  customerNutritionalFoodFeedbackId: number | null;
  enableApiCalls: boolean;
  changeDetection: ReturnType<typeof useDetectChanges>;
};

const nutritionalTags = [
  {
    id: tags.LessSugar,
    label: 'Less sugar',
  },
  {
    id: tags.HighProtein,
    label: 'More protein',
  },
  {
    id: tags.CarbConscious,
    label: 'Less carbs',
  },
  {
    id: tags.LowSodium,
    label: 'Less sodium',
  },
  {
    id: tags.LessFat,
    label: 'Less fat',
  },
  {
    id: tags.LowCalorie,
    label: 'Less calories',
  },
  {
    id: tags.LessCholesterol,
    label: 'Less cholesterol',
  },
  {
    id: tags.MoreFiber,
    label: 'More fiber',
  },
] as const;

export function FoodFeedbackSecondary(props: FoodFeedbackSecondaryProps) {
  const {
    foodFeedbackSelection,
    rating,
    item,
    productRatingId,
    customerNutritionalFoodFeedbackId,
    enableApiCalls,
    changeDetection,
  } = props;

  const { Nutrition, Ingredients, Other } = foodFeedbackIdMap;

  const handleIntersection = useCallback(
    (inView: boolean, entry: IntersectionObserverEntry) => {
      if (!inView && entry?.target) {
        entry.target.scrollIntoView({ behavior: 'smooth', block: 'end' });
      }
    },
    []
  );

  let heading;
  let secondary;

  if (
    foodFeedbackSelection === Ingredients &&
    rating &&
    'ingredients' in item
  ) {
    heading = 'Which ingredient(s) did you like?';
    secondary = (
      <IngredientsPillGroup
        item={item}
        key={`${foodFeedbackSelection}-${rating}`}
        rating={rating}
        enableApiCalls={enableApiCalls}
        changeDetection={changeDetection}
      />
    );
  } else if (
    foodFeedbackSelection === Ingredients &&
    !rating &&
    'ingredients' in item
  ) {
    heading = "Which ingredient(s) didn't you like?";
    secondary = (
      <IngredientsPillGroup
        item={item}
        key={`${foodFeedbackSelection}-${rating}`}
        rating={rating}
        enableApiCalls={enableApiCalls}
        changeDetection={changeDetection}
      />
    );
  } else if (foodFeedbackSelection === Nutrition && rating === false) {
    heading = 'How can we better support your nutrition?';
    secondary = (
      <NutritionalTagsPillGroup
        enableApiCalls={enableApiCalls}
        customerNutritionalFoodFeedbackId={customerNutritionalFoodFeedbackId}
        changeDetection={changeDetection}
      />
    );
  } else if (foodFeedbackSelection === Other) {
    heading = 'Leave more feedback';
    secondary = (
      <TextAreaFeedback
        rating={rating}
        productRatingId={productRatingId}
        item={item}
        enableApiCalls={enableApiCalls}
      />
    );
  } else {
    return null;
  }

  return (
    <div
      className={styles['food-feedback-secondary-container']}
      data-testid="food-feedback-secondary-container"
    >
      <Heading variant="H6">{heading}</Heading>
      {secondary}
      <InView as="div" triggerOnce onChange={handleIntersection} />
    </div>
  );
}

type IngredientsPillGroupProps = {
  item: RatablePairingItem;
  rating: boolean;
  enableApiCalls: boolean;
  onError?: () => void;
  changeDetection: ReturnType<typeof useDetectChanges>;
};

function IngredientsPillGroup(props: IngredientsPillGroupProps) {
  const { item, rating, enableApiCalls, onError, changeDetection } = props;

  return (
    <ToggleButtonPillGroup className={styles['secondary-element']}>
      {item.ingredients.map((ingredient) => {
        return (
          <IngredientsPillGroupItem
            key={ingredient.id}
            rating={rating}
            item={item}
            ingredient={ingredient}
            enableApiCalls={enableApiCalls}
            onError={onError}
            changeDetection={changeDetection}
          />
        );
      })}
    </ToggleButtonPillGroup>
  );
}

type IngredientsPillGroupItemProps = {
  rating: boolean;
  item: RatablePairingItem;
  enableApiCalls: boolean;
  onError?: () => void;
  ingredient: RatablePairingItem['ingredients'][number];
  changeDetection: ReturnType<typeof useDetectChanges>;
};

function IngredientsPillGroupItem(props: IngredientsPillGroupItemProps) {
  const { item, rating, ingredient, enableApiCalls, onError, changeDetection } =
    props;

  const { toggled, onToggle } = useIngredientFeedback({
    item,
    rating,
    ingredient,
    enabled: enableApiCalls,
    onError,
    changeDetection,
  });

  return (
    <ToggleButtonPill
      label={ingredient.name}
      isSelected={toggled}
      onChange={onToggle}
    />
  );
}

type NutritionalTagsPillGroupProps = {
  enableApiCalls: boolean;
  onError?: () => void;
  customerNutritionalFoodFeedbackId: number | null;
  changeDetection: ReturnType<typeof useDetectChanges>;
};

function NutritionalTagsPillGroup(props: NutritionalTagsPillGroupProps) {
  const { onError, customerNutritionalFoodFeedbackId, changeDetection } = props;

  const { selectedTagIds, onToggle } = useNutritionalTagFeedback({
    onError,
    customerNutritionalFoodFeedbackId,
  });

  return (
    <ToggleButtonPillGroup className={styles['secondary-element']}>
      {nutritionalTags.map((nutritionalTag) => {
        return (
          <ToggleButtonPill
            key={nutritionalTag.id}
            label={nutritionalTag.label}
            isSelected={selectedTagIds.has(nutritionalTag.id)}
            onChange={(isSelected) => {
              onToggle({ isSelected, tagId: nutritionalTag.id });

              changeDetection.updateFoodFeedbackSecondary({
                foodFeedbackId: foodFeedbackIdMap.Nutrition,
                foodFeedbackSecondaryId: nutritionalTag.id,
                foodFeedbackSecondaryType: 'tag',
                prevValue: !isSelected,
                newValue: isSelected,
              });
            }}
          />
        );
      })}
    </ToggleButtonPillGroup>
  );
}

type TextAreaFeedbackProps = {
  rating: boolean;
  productRatingId?: number | null;
  item: RatablePairingItem | RatableProductItem;
  enableApiCalls: boolean;
  onError?: () => void;
};

function TextAreaFeedback(props: TextAreaFeedbackProps) {
  const { rating, productRatingId, item, enableApiCalls, onError } = props;

  const { submitFeedback } = useTextAreaFeedback({
    rating,
    productRatingId,
    item,
    enabled: enableApiCalls,
    onError,
  });

  return (
    <div className={styles['secondary-element']}>
      <TextArea
        onChange={submitFeedback}
        onBlur={(e) => submitFeedback((e.target as HTMLTextAreaElement).value)}
        aria-label="Add text feedback"
        placeholder="I thought the flavor was..."
      />
    </div>
  );
}
